<script setup lang="ts">
type Props = {
  contentClass?: string;
};

const { contentClass } = defineProps<Props>();

const { loadingMessage } = useKyc();
const { $kyc } = useNuxtApp();

const { forceRequestKycReason } = useUserData();

const $style = useCssModule();

const $submitBtn = computed<string>(() => {
  if (!contentClass) {
    return `btn-primary ${$style.submitBtn}`;
  }

  return contentClass;
});

onMounted(() => {
  $kyc.resetEnvironment();
});
</script>

<template>
  <BaseKycIframe>
    <template #activator="{ startKyc, manualRefresh, step, isLoading }">
      <template
        v-if="
          step === 'initial' ||
          step === 'failed' ||
          step === 'expired' ||
          step === 'retry' ||
          step === 'internal_start_error'
        "
      >
        <button
          :class="$submitBtn"
          @click="startKyc"
        >
          <template v-if="isLoading">
            <NuxtIcon
              :class="$style.spinner"
              filled
              name="loading"
            />
          </template>

          <slot>
            <template v-if="step === 'initial'">
              <span>{{ $t('verification.init') }}</span>
            </template>

            <template v-else>
              <span>{{ $t('verification.restart') }}</span>
            </template>

            <NuxtIcon name="arrows/angle_right" />
          </slot>
        </button>
      </template>

      <div
        v-if="step === 'internal_status_error'"
        class="alert alert-warning mb-4"
      >
        <span>{{ $t('verification.kyc.internal_status_error') }}</span>
      </div>

      <template
        v-if="step === 'manual_refresh' || step === 'internal_status_error'"
      >
        <button
          :class="$submitBtn"
          @click="manualRefresh"
        >
          <template v-if="isLoading">
            <NuxtIcon
              :class="$style.spinner"
              filled
              name="loading"
            />
          </template>

          <slot>
            <span>{{ $t('verification.check_again') }}</span>

            <NuxtIcon name="arrows/angle_right" />
          </slot>
        </button>
      </template>

      <template v-if="step === 'started'">
        <BaseLoading
          :full-screen="false"
          inline
          show
        />
      </template>

      <template v-if="step === 'checking'">
        <BaseLoading
          :full-screen="false"
          inline
          show
        />

        <p :class="$style.loadingMessage">
          <span>{{ loadingMessage }}</span>
        </p>
      </template>

      
    </template>
  </BaseKycIframe>
</template>

<style module lang="postcss">
.submitBtn {
  @apply mx-auto rounded-md gap-1.5 flex items-center font-bold uppercase py-2.5 focus:outline-none focus:ring-4 appearance-none;
}

.disabledButton {
  @apply cursor-not-allowed opacity-50;

  &:hover {
    @apply cursor-not-allowed opacity-50;
  }
}

.loadingMessage {
  @apply text-sm font-semibold mt-2;
}

.spinner {
  animation: rotate 600ms infinite linear;
}

@keyframes rotate {
  to {
    transform: rotate(1turn);
  }
}
</style>
